import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Appointment } from '../../types';

interface Props {
  appointment: Appointment;
  onEdit: (appointment: Appointment) => void;
  onDuplicate: (appointment: Appointment) => void;
  onCancel: (appointment: Appointment) => void;
  onDelete: (appointment: Appointment) => void;
}

// Four icon buttons on every row is four chances to hit the wrong one, and two
// of them are destructive. They live behind one control now, with the
// destructive pair separated by a rule and named for what they do rather than
// left to a red icon to imply it.
//
// Cancel and Delete open the shared ConfirmDialog from the caller — the menu
// only decides what is asked, never does the acting.
const AppointmentRowActions: React.FC<Props> = ({
  appointment,
  onEdit,
  onDuplicate,
  onCancel,
  onDelete,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const close = () => setAnchorEl(null);
  const run = (action: (a: Appointment) => void) => () => {
    close();
    action(appointment);
  };

  const isCancelled = String(appointment.status || '').toLowerCase() === 'cancelled';

  return (
    <>
      <Tooltip title="Actions for this appointment" placement="top" arrow enterDelay={400}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={`Actions for ${appointment.patientName || 'this appointment'}`}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 210 } } }}
      >
        <MenuItem onClick={run(onEdit)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Edit appointment" secondary="Change the time, provider or notes" />
        </MenuItem>

        <MenuItem onClick={run(onDuplicate)}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText
            primary="Duplicate"
            secondary="Opens a new appointment with the same details"
          />
        </MenuItem>

        <Divider />

        {/* Cancelling an appointment that is already cancelled does nothing,
            so it is not offered. */}
        {!isCancelled && (
          <MenuItem onClick={run(onCancel)}>
            <ListItemIcon><CancelIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText
              primary="Cancel appointment"
              secondary="Frees the slot. The record stays"
            />
          </MenuItem>
        )}

        <MenuItem onClick={run(onDelete)}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText
            primary="Delete"
            secondary="Removes the record entirely"
            primaryTypographyProps={{ color: 'error.main' }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default AppointmentRowActions;
